import React, { useEffect, useRef, useState } from "react";
import SchoolIcon from "@material-ui/icons/School";
import PeopleIcon from "@material-ui/icons/People";
import EmojiPeopleIcon from "@material-ui/icons/EmojiPeople";
import FavoriteIcon from "@material-ui/icons/Favorite";
import "./ClientsSection.css";

const SCHOOLS = [
  { id: "future", name: "Future Leaders School", color: "#1769d6", soft: "#eef4ff", border: "#93c5fd" },
  { id: "discovery", name: "Discovery Learning Center", color: "#0d9488", soft: "#ecfdf5", border: "#5eead4" },
  { id: "harmony", name: "Harmony Montessori", color: "#7c4dff", soft: "#f5f3ff", border: "#c4b5fd" },
  { id: "bright", name: "Bright Minds School", color: "#ea580c", soft: "#fff7ed", border: "#fdba74" },
  { id: "horizon", name: "New Horizon School", color: "#16a34a", soft: "#f0fdf4", border: "#86efac" },
  { id: "stars", name: "Little Stars Academy", color: "#db2777", soft: "#fdf2f8", border: "#f9a8d4" },
  { id: "royaloak", name: "Royal Oak International Montessori", color: "#0e7490", soft: "#ecfeff", border: "#67e8f9" },
  { id: "arunodaya", name: "Arunodaya Montessori School", color: "#c026d3", soft: "#fdf4ff", border: "#f0abfc" },
  { id: "floreo", name: "Floreo Montessori", color: "#ca8a04", soft: "#fefce8", border: "#fde047" },
  { id: "focus", name: "Focus High School", color: "#dc2626", soft: "#fef2f2", border: "#fca5a5" },
  { id: "mhouse", name: "Montessori House of Children", color: "#4f46e5", soft: "#eef2ff", border: "#a5b4fc" },
];

const STATS = [
  { label: "Schools Served", target: 15, suffix: "+", icon: SchoolIcon, color: "#1769d6", soft: "#eef4ff" },
  { label: "Active Students", target: 1000, suffix: "", icon: PeopleIcon, color: "#2e7d32", soft: "#f0fdf4" },
  { label: "Teachers", target: 100, suffix: "", icon: EmojiPeopleIcon, color: "#d97706", soft: "#fff7ed" },
  { label: "Satisfaction", target: 100, suffix: "%", icon: FavoriteIcon, color: "#7c4dff", soft: "#f5f3ff" },
];

const AnimatedStatValue = ({ target, suffix = "", duration = 2000 }) => {
  const ref = useRef(null);
  const hasAnimated = useRef(false);
  const [display, setDisplay] = useState(0);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplay(target);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [target]);

  useEffect(() => {
    if (!active) return undefined;

    let startTime;
    let rafId;

    const tick = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - (1 - progress) ** 3;
      setDisplay(Math.round(eased * target));
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [active, target, duration]);

  return (
    <p
      ref={ref}
      className={`community__stat-value${active && display >= target ? " community__stat-value--done" : ""}`}
    >
      {display.toLocaleString()}
      {suffix}
    </p>
  );
};

const ClientsSection = () => {
  const scrollingSchools = [...SCHOOLS, ...SCHOOLS];

  return (
    <section className="community">
      <div className="community__inner">
        <header className="community__header">
          <p className="community__eyebrow">Growing together</p>
          <h2 className="community__title">Our Montestory Community</h2>
          <p className="community__lead">
            Trusted by leading Montessori schools worldwide
          </p>
        </header>

        <div className="community__marquee-wrap">
          <div className="community__marquee-fade community__marquee-fade--left" aria-hidden />
          <div className="community__marquee-fade community__marquee-fade--right" aria-hidden />
          <div className="community__marquee">
            {scrollingSchools.map((school, index) => {
              const initials = school.name
                .split(" ")
                .filter((w) => w.length > 2)
                .slice(0, 2)
                .map((w) => w[0])
                .join("")
                .toUpperCase();
              return (
              <article
                key={`${school.id}-${index}`}
                className={`community__school-card community__school-card--${school.id}`}
                style={{
                  "--school-accent": school.color,
                  "--school-soft": school.soft,
                  "--school-border": school.border,
                }}
              >
                <span className="community__school-avatar" aria-hidden>{initials}</span>
                <span className="community__school-name">{school.name}</span>
              </article>
            );
            })}
          </div>
        </div>

        <div className="community__stats">
          {STATS.map(({ label, target, suffix, icon: Icon, color, soft }) => (
            <article
              key={label}
              className="community__stat"
              style={{ "--stat-accent": color, "--stat-soft": soft }}
            >
              <span className="community__stat-icon" aria-hidden>
                <Icon />
              </span>
              <AnimatedStatValue target={target} suffix={suffix} />
              <p className="community__stat-label">{label}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ClientsSection;
